/**
 * Seasonal "active window" defaults per system kind.
 *
 * Many systems are inherently seasonal — a pool that's winterized doesn't
 * need weekly chemistry checks in January, and a snow blower doesn't need
 * blade maintenance in July. We store an optional active window (start /
 * end month, 1-12) on each system row so the plan generator and the UI can
 * suppress out-of-season noise.
 *
 * A null window means "year-round" (default). When a system is added, we
 * pick a sensible default based on its kind; users can override it on the
 * edit screen.
 */

export interface SeasonalRange {
  startMonth: number | null; // 1-12 or null
  endMonth: number | null; // 1-12 or null
}

const DEFAULTS: Record<string, SeasonalRange> = {
  pool: { startMonth: 5, endMonth: 9 }, // May–Sep
  mower: { startMonth: 4, endMonth: 10 }, // Apr–Oct
  snow_blower: { startMonth: 11, endMonth: 3 }, // Nov–Mar (wraps year)
  leaf_blower: { startMonth: 9, endMonth: 12 }, // Sep–Dec
  trimmer: { startMonth: 4, endMonth: 10 }, // Apr–Oct
  pressure_washer: { startMonth: 4, endMonth: 10 }, // Apr–Oct
  fire_pit: { startMonth: 4, endMonth: 10 }, // Apr–Oct
  irrig: { startMonth: 4, endMonth: 10 }, // Apr–Oct
};

/**
 * Climate-aware default seasonal window. For pool the swim season is
 * dramatically shorter in cold zones (Memorial Day to Labor Day, roughly
 * Jun–Sep) and effectively year-round in hot zones. Other kinds use the
 * same default everywhere — mower/blower seasons hinge on grass and
 * snow which the keyed_event-scheduled tasks already adapt to.
 *
 * Pass the user's climate zone when available; falls back to the
 * temperate default when null.
 */
export function defaultSeasonalRange(
  kind: string,
  climate?: string | null
): SeasonalRange {
  if (kind === 'pool') {
    if (climate === 'cold-humid' || climate === 'cold-dry') {
      return { startMonth: 6, endMonth: 9 }; // Jun–Sep (Memorial Day → Labor Day)
    }
    if (climate === 'hot-humid' || climate === 'hot-dry') {
      return { startMonth: 1, endMonth: 12 }; // year-round
    }
    // mixed / marine / unknown — fall through to the temperate default.
  }
  return DEFAULTS[kind] ?? { startMonth: null, endMonth: null };
}

const MONTH_NAME: string[] = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function seasonalLabel(range: SeasonalRange, isActive = true): string | null {
  if (!isActive) return 'Disabled';
  if (range.startMonth == null || range.endMonth == null) return null;
  return `Active ${MONTH_NAME[range.startMonth]}–${MONTH_NAME[range.endMonth]}`;
}

export function monthOption(n: number): string {
  return MONTH_NAME[n] ?? '';
}

/**
 * Returns true if the given month (1-12) falls within the system's active
 * window. Handles wrap-around (e.g. Nov–Mar). Null window = always active.
 */
export function monthInWindow(
  month: number,
  range: SeasonalRange
): boolean {
  if (range.startMonth == null || range.endMonth == null) return true;
  const { startMonth, endMonth } = range;
  if (startMonth <= endMonth) {
    return month >= startMonth && month <= endMonth;
  }
  // Wrap-around (Nov-Mar means months 11, 12, 1, 2, 3).
  return month >= startMonth || month <= endMonth;
}

export const MONTH_OPTIONS: { value: number; label: string }[] = Array.from(
  { length: 12 },
  (_, i) => ({ value: i + 1, label: MONTH_NAME[i + 1] })
);
